import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Navigate, Route, Routes } from "react-router-dom";

import { api } from "./api/client";
import { isHandedSession } from "./api/http";
import { AccountBadge, SupportBadge } from "./components/account";
import {
  IconAnalyzes,
  IconDesign,
  IconDevices,
  IconLibrary,
  IconModules,
  IconPackage,
  IconSettings,
  IconSignOut,
  IconTriggers,
} from "./components/icons";
import { Shell, type ShellNavGroup } from "./components/shell";
import { LanguageSwitcher, useI18n } from "./i18n";
import { useAuth } from "./hooks/useAuth";
import AgentApk from "./pages/AgentApk";
import Analyzes from "./pages/Analyzes";
import DevicePanel from "./pages/DevicePanel";
import Devices from "./pages/Devices";
import Login from "./pages/Login";
import Modules from "./pages/Modules";
import ReadyBuilds from "./pages/ReadyBuilds";
import ScreenDesignEditor from "./pages/ScreenDesignEditor";
import ScreenDesigns from "./pages/ScreenDesigns";
import Settings from "./pages/Settings";
import Triggers from "./pages/Triggers";

// TODO(scenarios/runs/policies): the Scenarios, Runs and Policies pages are
// parked while the features are reimplemented; the API client and the types
// for them are kept in api/client.ts and api/types.ts.
//
// The nav is grouped by the question being asked rather than by feature:
// "what are my phones doing", "what do I put on them", "who am I". Devices is
// the landing page — the fleet list is the whole of what an operator needs at
// a glance, so there is no separate dashboard above it.
const NAV: ShellNavGroup[] = [
  {
    label: "nav.group.fleet",
    items: [
      { path: "/", label: "nav.devices", icon: IconDevices, end: true },
      { path: "/triggers", label: "nav.triggers", icon: IconTriggers },
      { path: "/analyzes", label: "nav.analyzes", icon: IconAnalyzes },
    ],
  },
  {
    label: "nav.group.rollout",
    items: [
      { path: "/agent-apk", label: "nav.agentApk", icon: IconPackage },
      { path: "/ready-apks", label: "nav.readyApks", icon: IconLibrary },
      { path: "/design", label: "nav.design", icon: IconDesign },
      { path: "/modules", label: "nav.modules", icon: IconModules },
    ],
  },
  {
    label: "nav.group.account",
    items: [{ path: "/settings", label: "nav.settings", icon: IconSettings }],
  },
];

export default function App() {
  const queryClient = useQueryClient();
  const { t } = useI18n();
  const { token, refusal, signedIn, signOut } = useAuth();
  const me = useQuery({ queryKey: ["me"], queryFn: api.me, retry: false, enabled: !!token });

  const endSession = () => {
    signOut();
    queryClient.clear();
  };

  if (!token || me.isError) {
    return (
      <>
        <LanguageSwitcher floating />
        <Login
          notice={refusal}
          onSignedIn={() => {
            queryClient.clear();
            signedIn();
          }}
        />
      </>
    );
  }

  return (
    <Shell
      brandName={t("app.name")}
      groups={NAV}
      banner={
        // Only in a tab the admin console opened (B2B §7.1). `me` is what
        // names the customer, so the strip appears with the rest of the shell
        // rather than a moment before it and a moment before it can say whose.
        isHandedSession() && me.data ? (
          <SupportBadge
            email={me.data.email}
            tenant={me.data.tenant.name}
            onEnd={() => {
              endSession();
              // The tab was opened by a script, so it is allowed to close
              // itself. When the browser refuses — a tab reopened from history
              // has no opener — the login screen is already behind it.
              window.close();
            }}
            t={t}
          />
        ) : null
      }
      footer={
        <>
          {me.data?.tenant && <AccountBadge tenant={me.data.tenant} t={t} />}
          <button type="button" className="sign-out" onClick={endSession}>
            <IconSignOut size={16} />
            {t("app.signOut")}
          </button>
        </>
      }
    >
      <Routes>
        <Route path="/" element={<Devices />} />
        {/* Kept so links and bookmarks made while Devices lived here resolve. */}
        <Route path="/devices" element={<Navigate to="/" replace />} />
        <Route path="/devices/:id" element={<DevicePanel />} />
        <Route path="/triggers" element={<Triggers />} />
        <Route path="/analyzes" element={<Analyzes />} />
        <Route path="/agent-apk" element={<AgentApk />} />
        <Route path="/ready-apks" element={<ReadyBuilds />} />
        <Route path="/design" element={<ScreenDesigns />} />
        <Route path="/design/:id" element={<ScreenDesignEditor />} />
        <Route path="/modules" element={<Modules />} />
        <Route path="/settings" element={<Settings />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Shell>
  );
}
